import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { Modal, Platform, Pressable, ScrollView, StyleSheet, Switch, Text, TextInput, View } from "react-native";
import { NavigationContext } from "@react-navigation/native";
import MaterialIcons from "react-native-vector-icons/MaterialIcons";
import { useSelector } from "react-redux";
import CrossPlatformDropdown from "../../../components/CrossPlatformDropdown";
import { selectOrgTypes } from "../../../store/slices/masterDataSlice";
import AdditionalFieldsModal, { Field } from "./AdditionalFieldsModal";

export interface WizardTertiary {
  id: string;
  name: string;
}

export interface WizardSubcategory {
  id: string;
  name: string;
  noTertiary: boolean;
  tertiaries: WizardTertiary[];
}

export interface CategoryCreationDraft {
  categoryName: string;
  noSubcategories: boolean;
  subcategories: WizardSubcategory[];
  /** Keyed by wizardKeys.* */
  assignments: Record<string, { organization: string; team: string }>;
  /** Keyed by wizardKeys.* */
  fields: Record<string, Field[]>;
}

export interface WizardTeamOption {
  id: number;
  name: string;
}

/** Stable keys shared by the wizard and the consumer that maps the draft to an API request. */
export const wizardKeys = {
  category: "category",
  subcategory: (subcategoryId: string) => `sub:${subcategoryId}`,
  tertiary: (subcategoryId: string, tertiaryId: string) => `ter:${subcategoryId}:${tertiaryId}`,
};

interface AddCategoryWizardProps {
  visible: boolean;
  onClose: () => void;
  onSave: (draft: CategoryCreationDraft) => void | Promise<void>;
  existingNames?: string[];
  teams?: WizardTeamOption[];
}

type Step = "category" | "subcategories" | "tertiary" | "review";
type RowKind = "category" | "subcategory" | "tertiary";
type ReviewRow = {
  key: string;
  kind: RowKind;
  subcategory?: WizardSubcategory;
  tertiary?: WizardTertiary;
};
/** Which part of the tree is being edited after jumping back from the review screen. */
type EditScope = { kind: RowKind; subcategoryId?: string } | null;

const CATEGORY_NAME_MAX_LENGTH = 30;
const CATEGORY_NAME_MIN_LENGTH = 2;

let idSeq = 0;
const nextId = () => `w${(idSeq += 1)}`;

const emptyTertiary = (): WizardTertiary => ({ id: nextId(), name: "" });

const emptySubcategory = (): WizardSubcategory => ({
  id: nextId(),
  name: "",
  noTertiary: false,
  tertiaries: [emptyTertiary(), emptyTertiary()],
});

interface CancelConfirmationModalProps {
  visible: boolean;
  onKeepEditing: () => void;
  onDiscard: () => void;
}

const CancelConfirmationModal: React.FC<CancelConfirmationModalProps> = ({
  visible,
  onKeepEditing,
  onDiscard,
}) => (
  <Modal visible={visible} transparent animationType="fade" onRequestClose={onKeepEditing}>
    <View style={styles.confirmationOverlay}>
      <Pressable style={StyleSheet.absoluteFill} onPress={onKeepEditing} />
      <View style={styles.confirmationModal}>
        <View style={styles.confirmationHeader}>
          <View style={styles.confirmationHeading}>
            <View style={styles.warningIcon}>
              <MaterialIcons name="warning" size={22} color="#E87500" />
            </View>
            <Text style={styles.confirmationTitle}>Confirmation</Text>
          </View>
          <Pressable onPress={onKeepEditing} style={styles.close}>
            <MaterialIcons name="close" size={28} color="#202020" />
          </Pressable>
        </View>
        <View style={styles.confirmationBody}>
          <Text style={styles.confirmationMessage}>
            Are you sure you want to cancel? All progress will be lost
          </Text>
        </View>
        <View style={styles.confirmationFooter}>
          <Pressable style={styles.confirmationNoButton} onPress={onKeepEditing}>
            <Text style={styles.backText}>No</Text>
          </Pressable>
          <Pressable style={styles.confirmationYesButton} onPress={onDiscard}>
            <Text style={styles.nextText}>Yes</Text>
          </Pressable>
        </View>
      </View>
    </View>
  </Modal>
);

interface FieldsButtonProps {
  count?: number;
  onPress: () => void;
}

const FieldsButton: React.FC<FieldsButtonProps> = ({ count = 0, onPress }) => (
  <Pressable style={styles.fieldsButton} onPress={onPress}>
    <Text style={styles.fieldsButtonText}>{`+ Add Fields${count ? ` (${count})` : ""}`}</Text>
  </Pressable>
);

const AddCategoryWizard: React.FC<AddCategoryWizardProps> = ({
  visible,
  onClose,
  onSave,
  existingNames = [],
  teams = [],
}) => {
  const orgTypes = useSelector(selectOrgTypes);
  // NavigationContext is undefined (instead of throwing) when rendered outside a navigator.
  const navigation = useContext(NavigationContext);
  const pendingNavigationAction = useRef<any>(null);
  const skipNavigationGuard = useRef(false);

  const [step, setStep] = useState<Step>("category");
  const [categoryName, setCategoryName] = useState("");
  const [noSubcategories, setNoSubcategories] = useState(false);
  const [subcategories, setSubcategories] = useState<WizardSubcategory[]>(() => [emptySubcategory(), emptySubcategory()]);
  const [subcategoryIndex, setSubcategoryIndex] = useState(0);
  const [assignments, setAssignments] = useState<CategoryCreationDraft["assignments"]>({});
  const [fields, setFields] = useState<CategoryCreationDraft["fields"]>({});
  const [fieldEditorKey, setFieldEditorKey] = useState<string | null>(null);
  // Snapshot of the fields handed to the Additional Fields modal when it opens. Keeping it stable
  // stops wizard re-renders (e.g. after a delete) from resetting the modal's unsaved edits.
  const [fieldEditorInitialFields, setFieldEditorInitialFields] = useState<Field[]>([]);
  const [saving, setSaving] = useState(false);
  const [showDiscardConfirm, setShowDiscardConfirm] = useState(false);
  const [editScope, setEditScope] = useState<EditScope>(null);

  const openFieldEditor = (key: string) => {
    setFieldEditorInitialFields((fields[key] || []).map((field) => ({ ...field })));
    setFieldEditorKey(key);
  };

  /** Deleting a field in the modal removes it from the wizard draft right away. */
  const deleteDraftField = (field: Field) => {
    if (!fieldEditorKey) return;
    const key = fieldEditorKey;
    setFields((current) => {
      const remaining = (current[key] || []).filter((item) => item.id !== field.id);
      const updated = { ...current };
      if (remaining.length) updated[key] = remaining;
      else delete updated[key];
      return updated;
    });
  };

  const reset = () => {
    setStep("category");
    setCategoryName("");
    setNoSubcategories(false);
    setSubcategories([emptySubcategory(), emptySubcategory()]);
    setSubcategoryIndex(0);
    setAssignments({});
    setFields({});
    setFieldEditorKey(null);
    setFieldEditorInitialFields([]);
    setSaving(false);
    setShowDiscardConfirm(false);
    setEditScope(null);
  };

  const discard = () => {
    const action = pendingNavigationAction.current;
    pendingNavigationAction.current = null;
    reset();
    onClose();
    if (action && navigation) {
      // Let the pending navigation (user navigated away) continue without prompting again.
      skipNavigationGuard.current = true;
      navigation.dispatch(action);
    }
  };

  const keepEditing = () => {
    pendingNavigationAction.current = null;
    setShowDiscardConfirm(false);
  };

  const close = () => setShowDiscardConfirm(true);

  // Re-arm the navigation guard every time the wizard opens.
  useEffect(() => {
    if (visible) skipNavigationGuard.current = false;
  }, [visible]);

  // Navigating away from the page while the wizard is open must ask for confirmation.
  useEffect(() => {
    if (!visible || !navigation) return undefined;
    return navigation.addListener("beforeRemove", (event: any) => {
      if (skipNavigationGuard.current) return;
      event.preventDefault();
      pendingNavigationAction.current = event.data?.action ?? null;
      setShowDiscardConfirm(true);
    });
  }, [visible, navigation]);

  // Browser refresh / tab close / address-bar navigation on web.
  useEffect(() => {
    if (!visible || Platform.OS !== "web" || typeof window === "undefined") return undefined;
    const handler = (event: any) => {
      event.preventDefault();
      event.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [visible]);

  const trimmedCategoryName = categoryName.trim();
  const duplicateCategory = Boolean(trimmedCategoryName) && existingNames.some(
    (name) => name.trim().toLowerCase() === trimmedCategoryName.toLowerCase()
  );
  const validSubcategories = useMemo(
    () => subcategories.filter((subcategory) => subcategory.name.trim()),
    [subcategories]
  );
  const currentSubcategory: WizardSubcategory | undefined = validSubcategories[subcategoryIndex];

  const reviewRows = useMemo<ReviewRow[]>(() => {
    const rows: ReviewRow[] = [{ key: wizardKeys.category, kind: "category" }];
    if (noSubcategories) return rows;
    validSubcategories.forEach((subcategory) => {
      rows.push({ key: wizardKeys.subcategory(subcategory.id), kind: "subcategory", subcategory });
      if (subcategory.noTertiary) return;
      subcategory.tertiaries
        .filter((tertiary) => tertiary.name.trim())
        .forEach((tertiary) => {
          rows.push({
            key: wizardKeys.tertiary(subcategory.id, tertiary.id),
            kind: "tertiary",
            subcategory,
            tertiary,
          });
        });
    });
    return rows;
  }, [validSubcategories, noSubcategories]);

  const updateSubcategory = (id: string, update: Partial<WizardSubcategory>) => {
    setSubcategories((current) => current.map((item) => (item.id === id ? { ...item, ...update } : item)));
  };

  const updateTertiary = (subcategoryId: string, tertiaryId: string, name: string) => {
    setSubcategories((current) =>
      current.map((subcategory) =>
        subcategory.id !== subcategoryId
          ? subcategory
          : {
              ...subcategory,
              tertiaries: subcategory.tertiaries.map((tertiary) =>
                tertiary.id === tertiaryId ? { ...tertiary, name } : tertiary
              ),
            }
      )
    );
  };

  /**
   * Opens the review screen. Rows that have no assignment yet (e.g. children added while editing)
   * inherit their parent's Organization / Team so the user only has to adjust the differences.
   */
  const goToReview = () => {
    setAssignments((current) => {
      const filled = { ...current };
      reviewRows.forEach((row) => {
        if (row.kind === "category" || !row.subcategory) return;
        const parentKey = row.kind === "tertiary" ? wizardKeys.subcategory(row.subcategory.id) : wizardKeys.category;
        const parent = filled[parentKey];
        if (!parent) return;
        const own = filled[row.key];
        const organization = own?.organization || parent.organization;
        const team = own?.team || parent.team;
        if (organization || team) filled[row.key] = { organization: organization || "", team: team || "" };
      });
      return filled;
    });
    setEditScope(null);
    setStep("review");
  };

  const isSingleNodeEdit = Boolean(editScope && editScope.kind !== "category");

  const next = () => {
    if (step === "category") {
      setStep("subcategories");
      return;
    }
    if (step === "subcategories") {
      if (noSubcategories) {
        goToReview();
        return;
      }
      if (!validSubcategories.length) return;
      if (isSingleNodeEdit) {
        // Editing a single subcategory: only walk through that subcategory's children.
        const index = validSubcategories.findIndex((subcategory) => subcategory.id === editScope?.subcategoryId);
        if (index < 0) {
          goToReview();
          return;
        }
        setSubcategoryIndex(index);
        setStep("tertiary");
        return;
      }
      setSubcategoryIndex(0);
      setStep("tertiary");
      return;
    }
    if (step === "tertiary") {
      if (isSingleNodeEdit) {
        goToReview();
        return;
      }
      const nextIndex = subcategoryIndex + 1;
      if (nextIndex < validSubcategories.length) {
        setSubcategoryIndex(nextIndex);
      } else {
        goToReview();
      }
    }
  };

  const back = () => {
    if (step === "subcategories") setStep("category");
    else if (step === "tertiary") {
      if (subcategoryIndex > 0 && !isSingleNodeEdit) setSubcategoryIndex((current) => current - 1);
      else setStep("subcategories");
    } else if (step === "review") {
      if (noSubcategories) setStep("subcategories");
      else {
        setSubcategoryIndex(Math.max(validSubcategories.length - 1, 0));
        setStep("tertiary");
      }
    }
  };

  const startEdit = (scope: NonNullable<EditScope>) => {
    setEditScope(scope);
    if (scope.kind === "category") {
      setStep("category");
    } else if (scope.kind === "subcategory") {
      setStep("subcategories");
    } else {
      const index = validSubcategories.findIndex((subcategory) => subcategory.id === scope.subcategoryId);
      setSubcategoryIndex(Math.max(index, 0));
      setStep("tertiary");
    }
  };

  const hasAssignment = (key: string) => Boolean(assignments[key]?.organization && assignments[key]?.team);

  const categoryNameValid = trimmedCategoryName.length >= CATEGORY_NAME_MIN_LENGTH && !duplicateCategory;
  const canNext =
    step === "category"
      ? categoryNameValid
      : step === "subcategories"
      ? noSubcategories || Boolean(validSubcategories.length)
      : step === "tertiary"
      ? Boolean(currentSubcategory?.noTertiary || currentSubcategory?.tertiaries.some((tertiary) => tertiary.name.trim()))
      : reviewRows.every((row) => hasAssignment(row.key));

  const save = async () => {
    if (!canNext || saving) return;
    setSaving(true);
    try {
      await onSave({
        categoryName: trimmedCategoryName,
        noSubcategories,
        subcategories: noSubcategories ? [] : validSubcategories,
        assignments,
        fields,
      });
      reset();
    } catch {
      // The caller reports the failure (toast); keep the wizard open so the user can retry.
    } finally {
      setSaving(false);
    }
  };

  /** Changing a parent's Organization / Team cascades to its children, which stay individually editable. */
  const setAssignment = (row: ReviewRow, field: "organization" | "team", value: string) => {
    setAssignments((current) => {
      const apply = (map: CategoryCreationDraft["assignments"], key: string) => ({
        ...map,
        [key]: {
          organization: map[key]?.organization || "",
          team: map[key]?.team || "",
          [field]: value,
        },
      });
      let updated = apply(current, row.key);
      reviewRows.forEach((candidate) => {
        const isChild =
          row.kind === "category"
            ? candidate.kind !== "category"
            : row.kind === "subcategory"
            ? candidate.kind === "tertiary" && candidate.subcategory?.id === row.subcategory?.id
            : false;
        if (isChild) updated = apply(updated, candidate.key);
      });
      return updated;
    });
  };

  const title =
    step === "category"
      ? "Add Ticket Category"
      : step === "subcategories"
      ? "Add Subcategories"
      : step === "tertiary"
      ? "Add Tertiary"
      : "Review Category Creation";

  const renderCategoryStep = () => (
    <View style={styles.field}>
      <Text style={styles.label}>
        Category Name <Text style={styles.required}>*</Text>
      </Text>
      <View style={styles.itemRow}>
        <TextInput
          autoFocus
          style={[styles.input, duplicateCategory && styles.errorInput]}
          placeholder="e.g. Facilities Request"
          value={categoryName}
          onChangeText={setCategoryName}
          maxLength={CATEGORY_NAME_MAX_LENGTH}
        />
        <FieldsButton count={fields[wizardKeys.category]?.length} onPress={() => openFieldEditor(wizardKeys.category)} />
      </View>
      {duplicateCategory && <Text style={styles.errorText}>Duplicate Category</Text>}
    </View>
  );

  const renderSubcategoriesStep = () => (
    <>
      <Text style={styles.contextLabel}>Category</Text>
      <Text style={styles.contextValue}>{categoryName}</Text>
      <View style={styles.switchRow}>
        <Text style={styles.label}>No Subcategories for this Category</Text>
        <Switch value={noSubcategories} onValueChange={setNoSubcategories} />
      </View>
      {noSubcategories
        ? [0, 1].map((index) => (
            <View style={styles.itemRow} key={`no-sub-${index}`}>
              <TextInput style={[styles.input, styles.disabledInput]} value="No Subcategories" editable={false} />
            </View>
          ))
        : subcategories.map((subcategory, index) => {
            const key = wizardKeys.subcategory(subcategory.id);
            return (
              <View style={styles.itemRow} key={subcategory.id}>
                <TextInput
                  style={styles.input}
                  placeholder={`Subcategory ${index + 1}`}
                  value={subcategory.name}
                  onChangeText={(name) => updateSubcategory(subcategory.id, { name })}
                />
                <FieldsButton count={fields[key]?.length} onPress={() => openFieldEditor(key)} />
              </View>
            );
          })}
      {!noSubcategories && (
        <Pressable style={styles.linkButton} onPress={() => setSubcategories((current) => [...current, emptySubcategory()])}>
          <Text style={styles.linkText}>+ Add Subcategory</Text>
        </Pressable>
      )}
    </>
  );

  const renderTertiaryStep = () =>
    currentSubcategory ? (
      <>
        <Text style={styles.contextLabel}>Category</Text>
        <Text style={styles.contextValue}>{categoryName}</Text>
        <Text style={styles.contextLabel}>Subcategory</Text>
        <Text style={styles.contextValue}>{currentSubcategory.name}</Text>
        <View style={styles.switchRow}>
          <Text style={styles.label}>No Tertiary for this Subcategory</Text>
          <Switch
            value={currentSubcategory.noTertiary}
            onValueChange={(value) => updateSubcategory(currentSubcategory.id, { noTertiary: value })}
          />
        </View>
        {currentSubcategory.noTertiary
          ? [0, 1].map((index) => (
              <View style={styles.itemRow} key={`no-ter-${index}`}>
                <TextInput style={[styles.input, styles.disabledInput]} value="No Tertiary" editable={false} />
              </View>
            ))
          : currentSubcategory.tertiaries.map((tertiary, index) => {
              const key = wizardKeys.tertiary(currentSubcategory.id, tertiary.id);
              return (
                <View style={styles.itemRow} key={tertiary.id}>
                  <TextInput
                    style={styles.input}
                    placeholder={`Tertiary ${index + 1}`}
                    value={tertiary.name}
                    onChangeText={(name) => updateTertiary(currentSubcategory.id, tertiary.id, name)}
                  />
                  <FieldsButton count={fields[key]?.length} onPress={() => openFieldEditor(key)} />
                </View>
              );
            })}
        {!currentSubcategory.noTertiary && (
          <Pressable
            style={styles.linkButton}
            onPress={() =>
              updateSubcategory(currentSubcategory.id, {
                tertiaries: [...currentSubcategory.tertiaries, emptyTertiary()],
              })
            }
          >
            <Text style={styles.linkText}>+ Add Tertiary</Text>
          </Pressable>
        )}
      </>
    ) : null;

  const renderReviewStep = () => (
    <>
      <Text style={styles.reviewHelp}>
        Confirm the Organization and Team Assignment for each new routing combination. Both are required.
      </Text>
      <View style={styles.tableHeader}>
        <Text style={styles.tableCategory}>Category</Text>
        <Text style={styles.tableSubcategory}>Subcategory</Text>
        <Text style={styles.tableTertiary}>Tertiary</Text>
        <Text style={styles.tableAssignment}>Organization</Text>
        <Text style={styles.tableAssignment}>Team Assignment</Text>
      </View>
      {reviewRows.map((row) => {
        const assignment = assignments[row.key];
        const selectedOrganization = assignment?.organization
          ? { id: assignment.organization, displayText: assignment.organization }
          : null;
        const selectedTeam = assignment?.team
          ? {
              id: assignment.team,
              displayText: teams.find((team) => String(team.id) === assignment.team)?.name || assignment.team,
            }
          : null;
        return (
          <View style={styles.reviewRow} key={row.key}>
            <View style={styles.reviewCategoryCell}>
              <Pressable onPress={() => startEdit({ kind: "category" })}>
                <MaterialIcons name="edit" size={16} color="#034895" />
              </Pressable>
              <Text style={styles.reviewName}>{categoryName}</Text>
            </View>
            <View style={styles.reviewSubcategoryCell}>
              {row.subcategory ? (
                <>
                  <Pressable onPress={() => startEdit({ kind: "subcategory", subcategoryId: row.subcategory?.id })}>
                    <MaterialIcons name="edit" size={16} color="#034895" />
                  </Pressable>
                  <Text style={styles.reviewName}>{row.subcategory.name}</Text>
                </>
              ) : (
                <Text style={styles.mutedDash}>—</Text>
              )}
            </View>
            <View style={styles.reviewTertiaryCell}>
              {row.tertiary ? (
                <>
                  <Pressable onPress={() => startEdit({ kind: "tertiary", subcategoryId: row.subcategory?.id })}>
                    <MaterialIcons name="edit" size={16} color="#034895" />
                  </Pressable>
                  <Text style={styles.reviewName}>{row.tertiary.name}</Text>
                </>
              ) : (
                <Text style={styles.mutedDash}>—</Text>
              )}
            </View>
            <CrossPlatformDropdown
              childList={orgTypes.map((organization) => ({ id: organization, displayText: organization }))}
              placeholder="Organization *"
              selection="single"
              isClearable
              selectedValue={selectedOrganization}
              setSelectedValue={(item) =>
                setAssignment(row, "organization", item && !Array.isArray(item) ? String(item.displayText) : "")
              }
              size="small"
              disableSorting
              style={styles.assignmentDropdown}
            />
            <CrossPlatformDropdown
              childList={teams.map((team) => ({ id: String(team.id), displayText: team.name }))}
              placeholder="Team Assignment *"
              selection="single"
              isClearable
              selectedValue={selectedTeam}
              setSelectedValue={(item) =>
                setAssignment(row, "team", item && !Array.isArray(item) ? String(item.id) : "")
              }
              size="small"
              disableSorting
              style={styles.assignmentDropdown}
            />
          </View>
        );
      })}
    </>
  );

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={close}>
      <View style={styles.overlay}>
        <Pressable style={StyleSheet.absoluteFill} onPress={close} />
        <View style={styles.modal}>
          <View style={styles.header}>
            <Text style={styles.title}>{title}</Text>
            <Pressable onPress={close} style={styles.close}>
              <MaterialIcons name="close" size={24} color="#034895" />
            </Pressable>
          </View>
          <ScrollView style={styles.body} contentContainerStyle={styles.bodyContent}>
            {step === "category" && renderCategoryStep()}
            {step === "subcategories" && renderSubcategoriesStep()}
            {step === "tertiary" && renderTertiaryStep()}
            {step === "review" && renderReviewStep()}
          </ScrollView>
          <View style={styles.footer}>
            <Pressable style={styles.cancel} onPress={close}>
              <Text style={styles.cancelText}>Cancel</Text>
            </Pressable>
            <View style={styles.footerRight}>
              {step !== "category" && (
                <Pressable style={styles.back} onPress={back}>
                  <Text style={styles.backText}>Back</Text>
                </Pressable>
              )}
              {editScope && step !== "review" && (
                <Pressable style={[styles.back, !canNext && styles.disabled]} onPress={goToReview} disabled={!canNext}>
                  <Text style={styles.backText}>Return</Text>
                </Pressable>
              )}
              <Pressable
                style={[styles.next, (!canNext || saving) && styles.disabled]}
                onPress={step === "review" ? save : next}
                disabled={!canNext || saving}
              >
                <Text style={styles.nextText}>{step === "review" ? (saving ? "Saving..." : "Save") : "Next"}</Text>
              </Pressable>
            </View>
          </View>
          <CancelConfirmationModal visible={showDiscardConfirm} onKeepEditing={keepEditing} onDiscard={discard} />
          <AdditionalFieldsModal
            key={fieldEditorKey ?? "none"}
            visible={fieldEditorKey !== null}
            onClose={() => setFieldEditorKey(null)}
            onSave={(savedFields) => {
              if (fieldEditorKey) {
                setFields((current) => {
                  const updated = { ...current };
                  if (savedFields.length) updated[fieldEditorKey] = savedFields;
                  else delete updated[fieldEditorKey];
                  return updated;
                });
              }
              setFieldEditorKey(null);
            }}
            onDeleteField={deleteDraftField}
            initialFields={fieldEditorInitialFields}
            subtitle="Category creation"
          />
        </View>
      </View>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: { flex: 1, backgroundColor: "rgba(20, 30, 50, 0.45)", justifyContent: "center", alignItems: "center" },
  modal: {
    backgroundColor: "#fff",
    borderRadius: 8,
    width: "min(640px, 92%)",
    maxHeight: "86%",
    ...Platform.select({ web: { boxShadow: "0 8px 24px rgba(20, 30, 50, 0.16)" } as any, default: { elevation: 8 } }),
  },
  confirmationOverlay: { flex: 1, backgroundColor: "rgba(20, 30, 50, 0.45)", justifyContent: "center", alignItems: "center", padding: 20 },
  confirmationModal: {
    backgroundColor: "#fff",
    borderRadius: 8,
    width: "min(360px, 92%)",
    ...Platform.select({ web: { boxShadow: "0 8px 24px rgba(20, 30, 50, 0.2)" } as any, default: { elevation: 8 } }),
  },
  confirmationHeader: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 14, paddingVertical: 12, borderBottomWidth: 1, borderBottomColor: "#E3E5E8" },
  confirmationHeading: { flexDirection: "row", alignItems: "center", gap: 10 },
  warningIcon: { width: 32, height: 32, borderRadius: 16, backgroundColor: "#FFE8D2", alignItems: "center", justifyContent: "center" },
  confirmationTitle: { fontSize: 18, fontWeight: "700", color: "#162235" },
  header: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", paddingHorizontal: 24, paddingVertical: 18, borderBottomWidth: 1, borderBottomColor: "#F0F0F0" },
  title: { fontSize: 18, fontWeight: "700", color: "#2D2D2D" },
  close: { padding: 4 },
  confirmationBody: { paddingHorizontal: 16, paddingVertical: 18 },
  confirmationMessage: { fontSize: 14, lineHeight: 20, color: "#424B59", textAlign: "left" },
  // "No" sits bottom-left, "Yes" bottom-right.
  confirmationFooter: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 8, paddingHorizontal: 14, paddingVertical: 12, borderTopWidth: 1, borderTopColor: "#E3E5E8" },
  confirmationNoButton: { minWidth: 90, alignItems: "center", paddingHorizontal: 16, paddingVertical: 8, borderWidth: 2, borderColor: "#D0D4D9", borderRadius: 100 },
  confirmationYesButton: { minWidth: 90, alignItems: "center", paddingHorizontal: 16, paddingVertical: 8, borderRadius: 100, backgroundColor: "#075DB5" },
  body: { paddingHorizontal: 24, maxHeight: Platform.OS === "web" ? ("58vh" as unknown as number) : 500 } as any,
  bodyContent: { paddingVertical: 16, gap: 12 },
  field: { gap: 6 },
  label: { fontSize: 12, fontWeight: "600", color: "#585858" },
  required: { color: "#D32F2F" },
  input: { flex: 1, height: 40, borderWidth: 1, borderColor: "#D9D9D9", borderRadius: 6, paddingHorizontal: 12, fontSize: 14, color: "#2D2D2D" },
  disabledInput: { backgroundColor: "#F3F3F3", color: "#9A9A9A", borderColor: "#E5E5E5" },
  errorInput: { borderColor: "#D32F2F" },
  errorText: { color: "#D32F2F", fontSize: 12 },
  contextLabel: { fontSize: 11, color: "#818181", marginTop: 4 },
  contextValue: { fontSize: 15, fontWeight: "700", color: "#333" },
  switchRow: { flexDirection: "row", alignItems: "center", justifyContent: "space-between", borderTopWidth: 1, borderBottomWidth: 1, borderColor: "#F0F0F0", paddingVertical: 10 },
  itemRow: { flexDirection: "row", alignItems: "center", gap: 8 },
  fieldsButton: { paddingHorizontal: 8, paddingVertical: 8 },
  fieldsButtonText: { color: "#034895", fontSize: 12, fontWeight: "700" },
  linkButton: { alignSelf: "flex-start", paddingVertical: 4 },
  linkText: { color: "#034895", fontSize: 13, fontWeight: "700" },
  reviewHelp: { fontSize: 11, color: "#818181", lineHeight: 16, marginBottom: 2 },
  tableHeader: { flexDirection: "row", borderBottomWidth: 1, borderColor: "#D9D9D9", paddingBottom: 8, gap: 6 },
  tableCategory: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" },
  tableSubcategory: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" },
  tableTertiary: { flex: 1.1, fontSize: 11, fontWeight: "700", color: "#585858" },
  tableAssignment: { flex: 1.2, fontSize: 11, fontWeight: "700", color: "#585858" },
  reviewRow: { flexDirection: "row", alignItems: "center", gap: 6, borderBottomWidth: 1, borderColor: "#F0F0F0", paddingVertical: 8 },
  reviewCategoryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 },
  reviewSubcategoryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 },
  reviewTertiaryCell: { flex: 1.1, flexDirection: "row", alignItems: "center", gap: 3 },
  reviewName: { flexShrink: 1, fontSize: 12, color: "#034895" },
  mutedDash: { color: "#D0D0D0", fontSize: 14 },
  assignmentDropdown: { flex: 1.2, minWidth: 0 },
  footer: { flexDirection: "row", justifyContent: "space-between", alignItems: "center", gap: 8, paddingHorizontal: 24, paddingVertical: 12, borderTopWidth: 1, borderTopColor: "#F0F0F0" },
  footerRight: { flexDirection: "row", alignItems: "center", gap: 8 },
  cancel: { padding: 10 },
  cancelText: { color: "#585858", fontWeight: "700" },
  back: { paddingHorizontal: 20, paddingVertical: 9, borderWidth: 1, borderColor: "#D9D9D9", borderRadius: 100 },
  backText: { color: "#034895", fontWeight: "700" },
  next: { backgroundColor: "#034895", paddingHorizontal: 24, paddingVertical: 9, borderRadius: 100 },
  disabled: { opacity: 0.5 },
  nextText: { color: "#fff", fontWeight: "700" },
});

export default AddCategoryWizard;
